import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { Spec, View } from "vega";
import { chartDetailForSize, type ChartDetail } from "../fit/detail.js";
import { EmbedFrame, useSafeEmbedOptions } from "../components/EmbedFrame.js";
import { useVizTheme, type UseVizThemeOptions } from "../theme/useVizTheme.js";
import type { ChartData } from "../types.js";
import { findEmptyDraw } from "./emptyDraw.js";
import { buildRuntimeChart, type RuntimeChart } from "./runtime.js";
import type { CustomChartGrammar, CustomChartSpec } from "./types.js";
import { validateCustomChart } from "./validate.js";

export interface CustomChartProps {
  /** The grammar {@link spec} is written in. */
  grammar: CustomChartGrammar;
  /** The persisted spec: untrusted JSON, validated before anything renders. */
  spec: unknown;
  /** The host's dataset, bound per the data contract (see `bindDataset`). */
  data: ChartData;
  /** Total chart height in pixels; width tracks the container. */
  height: number;
  /**
   * The container's width, when the host has measured it: sizes the first
   * paint and decides how much detail fits (`chartDetailForSize`).
   */
  width?: number;
  renderer?: "svg" | "canvas";
  tooltip?: boolean;
  /**
   * Theme overrides — pass a palette here to recolor both grammars. Keep the
   * object stable (memoize it): a new one re-renders the chart.
   */
  theme?: UseVizThemeOptions;
  className?: string;
  style?: CSSProperties;
  onNewView?: (view: View) => void;
  /**
   * Rendered instead of the chart when the spec is invalid or fails at
   * render time, with the reasons. Never throws into the host.
   */
  errorFallback: (errors: string[]) => ReactNode;
}

const FALLBACK_WIDTH = 300;

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

type Resolved =
  { ok: true; runtime: RuntimeChart } | { ok: false; errors: string[] };

/** Validates, then builds the runtime chart — either step's failure as reasons. */
function resolveChart(
  grammar: CustomChartGrammar,
  spec: unknown,
  build: (chart: CustomChartSpec) => RuntimeChart,
): Resolved {
  const validation = validateCustomChart({ grammar, spec });
  if (!validation.ok) return validation;
  try {
    return { ok: true, runtime: build(validation.chart) };
  } catch (error) {
    return { ok: false, errors: [messageOf(error)] };
  }
}

/**
 * The detail a chart of this size shows, stable while it stays the same —
 * rebuilt only when the detail that fits changes: the embed tracks the
 * container's width itself, so a resize alone needs no new spec.
 */
function useFittedDetail(width: number, height: number): ChartDetail {
  const { drawable, legend, tickLabels, axisTitles } = chartDetailForSize({
    width,
    height,
  });
  return useMemo<ChartDetail>(
    () => ({ drawable, legend, tickLabels, axisTitles }),
    [drawable, legend, tickLabels, axisTitles],
  );
}

/**
 * A chart that drew nothing from rows that exist is broken, not empty (see
 * findEmptyDraw): checked once each spec has drawn. Keyed by content, as the
 * embed is: an equal spec rebuilt by a re-resolved theme draws the same
 * nothing and is not re-embedded. `emptyReason` is set while the current spec
 * is the one that drew nothing.
 */
function useEmptyDrawCheck(
  runtimeSpec: Spec | null,
  rowCount: number,
  onNewView: ((view: View) => void) | undefined,
): { handleNewView: (view: View) => void; emptyReason: string | null } {
  const [emptyDraw, setEmptyDraw] = useState<{
    specKey: string;
    reason: string;
  } | null>(null);
  const specKey = useMemo(() => JSON.stringify(runtimeSpec), [runtimeSpec]);
  const handleNewView = useCallback(
    (view: View) => {
      onNewView?.(view);
      if (!runtimeSpec) return;
      const reason = findEmptyDraw(view, runtimeSpec, rowCount);
      setEmptyDraw(reason ? { specKey, reason } : null);
    },
    [onNewView, runtimeSpec, specKey, rowCount],
  );
  const emptyReason = emptyDraw?.specKey === specKey ? emptyDraw.reason : null;
  return { handleNewView, emptyReason };
}

/**
 * The chart resolved for its host — validated, bound to `data`, themed and
 * fitted to `size` — or why it can't be, and whether the theme is dark.
 */
function useRuntimeChart(
  grammar: CustomChartGrammar,
  spec: unknown,
  data: ChartData,
  { width, height }: { width: number; height: number },
  theme: CustomChartProps["theme"],
): { resolved: Resolved; isDark: boolean } {
  const { palette, isDark } = useVizTheme(theme);
  const detail = useFittedDetail(width, height);
  const themePalette = theme?.palette;
  const font = theme?.font;
  const resolved = useMemo(
    () =>
      resolveChart(grammar, spec, (chart) =>
        buildRuntimeChart(
          chart,
          data,
          { height, fallbackWidth: FALLBACK_WIDTH, detail },
          palette,
          { palette: themePalette, font },
        ),
      ),
    [grammar, spec, data, height, detail, palette, themePalette, font],
  );
  return { resolved, isDark };
}

/**
 * Renders a Custom chart in either grammar: validated (`validateCustomChart`),
 * bound to `data`, themed, sandboxed and fitted to its host through the same
 * runtime pipeline the headless tests render (`buildRuntimeChart`).
 */
export function CustomChart({
  grammar,
  spec,
  data,
  height,
  width = 0,
  renderer = "svg",
  tooltip = true,
  theme,
  className,
  style,
  onNewView,
  errorFallback,
}: CustomChartProps): ReactNode {
  const { resolved, isDark } = useRuntimeChart(
    grammar,
    spec,
    data,
    { width, height },
    theme,
  );

  const options = useSafeEmbedOptions({
    mode: "vega",
    renderer,
    actions: false,
    tooltip,
    isDark,
    config: resolved.ok ? resolved.runtime.config : undefined,
  });

  const runtimeSpec = resolved.ok ? resolved.runtime.spec : null;
  const { handleNewView, emptyReason } = useEmptyDrawCheck(
    runtimeSpec,
    data.length,
    onNewView,
  );

  if (!resolved.ok) return errorFallback(resolved.errors);
  const { runtime } = resolved;
  if (emptyReason) return errorFallback([emptyReason]);
  return (
    <EmbedFrame
      className={className}
      style={style}
      spec={runtime.spec}
      options={options}
      onNewView={handleNewView}
      errorFallback={(error) => errorFallback([messageOf(error)])}
    />
  );
}
